/**
 * Creates an event listener for Google Publisher Tags (GPT) ad events.
 *
 * @param eventType - The type of event to listen for (e.g., 'slotRenderEnded').
 * @param divId - The div ID of the ad slot.
 * @param listener - The callback function to execute when the event is triggered.
 * @param options - Optional parameters, such as `once` to remove the listener after the first invocation.
 * @returns A function to remove the event listener.
 */
export function createAdEventListener<
  K extends keyof googletag.events.EventTypeMap,
>(
  eventType: K,
  divId: string,
  listener: (arg: googletag.events.EventTypeMap[K]) => void,
  options: { once?: boolean } = {},
) {
  if (!window.googletag?.cmd) return;

  const eventListener = (event: googletag.events.EventTypeMap[K]) => {
    if (divId !== event.slot.getSlotElementId()) {
      // Don't call the listener callback if the event wasn't for the
      // specified divId.
      return;
    }

    listener(event);

    if (options.once) {
      window.googletag.pubads().removeEventListener(eventType, eventListener);
    }
  };

  window.googletag.cmd.push(() => {
    window.googletag.pubads().addEventListener(eventType, eventListener);
  });

  return () => {
    window.googletag.cmd.push(() =>
      window.googletag.pubads().removeEventListener(eventType, eventListener),
    );
  };
}
